import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { ParticipantKyc, ParticipantKycRole, ParticipantKycStatus } from '../../api/types';
import { StatusPill } from '../../components/ui/StatusPill';
import { kycStatusColor } from '../../components/domain/statusColor';
import styles from './ParticipantKycSection.module.css';

interface Props {
  /** `ShipmentResponse.participantKyc`: the users and KYC the trip-start check reads. */
  entries: ParticipantKyc[] | null | undefined;
  /** Return path for the profile links. */
  from: string;
}

const ROLE_LABEL: Record<ParticipantKycRole, string> = {
  SHIPPER: 'Shipper',
  DRIVER: 'Driver',
  PARTNER: 'Partner',
};

function statusDisplay(
  status: ParticipantKycStatus,
  role: ParticipantKycRole,
): { label: string; icon: string; bg: string; text: string } {
  switch (status) {
    case 'VERIFIED':
      return { label: 'Verified', icon: '✓', ...kycStatusColor('VERIFIED') };
    case 'SUBMITTED':
      return { label: 'Submitted', icon: '○', ...kycStatusColor('SUBMITTED') };
    case 'REJECTED':
      return { label: 'Rejected', icon: '✕', ...kycStatusColor('REJECTED') };
    case 'MISSING':
      return { label: `No ${ROLE_LABEL[role].toLowerCase()}`, icon: '!', ...kycStatusColor('REJECTED') };
    case 'PENDING':
    default:
      return { label: 'Pending', icon: '○', ...kycStatusColor('PENDING') };
  }
}

/** Trip-start KYC per participant, exactly as rhino-api checks it before a trip can start. */
export function ParticipantKycSection({ entries, from }: Props) {
  const { t } = useTranslation();
  if (!entries || entries.length === 0) return null;
  return (
    <section className={styles.section} aria-labelledby="participant-kyc-title">
      <h4 id="participant-kyc-title" className={styles.title}>
        {t('shipments.participantKycSection.title')}
      </h4>
      {entries.map((entry, index) => {
        const role = ROLE_LABEL[entry.role] ?? entry.role;
        const status = statusDisplay(entry.kycStatus, entry.role);
        const name = entry.name?.trim() || (entry.userId != null ? `${role} #${entry.userId}` : '—');
        return (
          <div
            key={`${entry.role}-${index}`}
            className={styles.row}
            data-testid={`participant-kyc-${entry.role}`}
          >
            <span className={styles.role} data-testid="participant-kyc-role">
              {role}
            </span>
            <span className={styles.name}>
              {entry.userId != null ? (
                <Link to={`/users/${entry.userId}`} state={{ from }} className={styles.link}>
                  {name}
                </Link>
              ) : (
                name
              )}
            </span>
            <StatusPill bg={status.bg} text={status.text} label={status.label} icon={status.icon} />
          </div>
        );
      })}
    </section>
  );
}
